import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { studioMessages } from '../studioMessages';
import { DetailField } from './DetailField';
import { ChoiceChip, ChoiceChips } from './ChoiceChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** `m:ss` of a song time. */
export function songTime(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** The Canvas lengths (`ASSET_SPECS` canvas: 3 to 8 s). */
const CANVAS_LENGTHS: ReadonlyArray<3 | 5 | 8> = [3, 5, 8];

interface SongWindowPickerProps {
  durationSec: number | null;
  hookStartSec: number | null;
  startSec: number;
  /** The length the window shows. */
  lengthSec: number;
  onStartChange: (startSec: number) => void;
  /** The length chips (3, 5, 8 s). Absent: no length choice (the Canvas is four bars, owner answer 2026-10-08). */
  onLengthChange?: (lengthSec: 3 | 5 | 8) => void;
}

/**
 * "Which part of the song" (design n2): a window over the song that starts at the hook, and the
 * length when the asset type offers one. The client has no beat grid, so the window moves by whole
 * seconds; the server snaps the plan to the downbeats.
 */
const SongWindowPickerComponent: React.FC<SongWindowPickerProps> = (props) => {
  const { durationSec, hookStartSec, startSec, lengthSec, onStartChange, onLengthChange } = props;
  const intl = useIntl();
  const max = Math.max(0, Math.floor((durationSec ?? startSec + lengthSec) - lengthSec));
  const lengthTitle = intl.formatMessage(studioMessages.lengthTitle);
  const lengths = useMemo<Array<ChoiceChip<3 | 5 | 8>>>(
    () =>
      CANVAS_LENGTHS.map((value) => ({ value, label: intl.formatMessage(studioMessages.seconds, { count: value }) })),
    [intl]
  );

  return (
    <>
      <DetailField
        title={intl.formatMessage(studioMessages.windowTitle)}
        hint={intl.formatMessage(studioMessages.windowHint)}
      >
        {hookStartSec !== null && (
          <span className="small text-muted">
            {intl.formatMessage(studioMessages.windowHook, { time: songTime(hookStartSec) })}
          </span>
        )}
        <input
          type="range"
          className={styles.window}
          min={0}
          max={max}
          step={1}
          value={Math.min(startSec, max)}
          aria-label={intl.formatMessage(studioMessages.windowLabel)}
          aria-valuetext={`${songTime(startSec)} – ${songTime(startSec + lengthSec)}`}
          onChange={(event) => onStartChange(Number(event.target.value))}
        />
        <div className={styles.windowScale}>
          <span>{songTime(0)}</span>
          <span>{`${songTime(startSec)} – ${songTime(startSec + lengthSec)}`}</span>
          {durationSec !== null && <span>{songTime(durationSec)}</span>}
        </div>
        {onLengthChange === undefined && (
          <span className="small text-muted">{intl.formatMessage(studioMessages.windowNote)}</span>
        )}
      </DetailField>
      {onLengthChange !== undefined && (
        <DetailField title={lengthTitle} hint={intl.formatMessage(studioMessages.windowNote)}>
          <ChoiceChips
            label={lengthTitle}
            options={lengths}
            value={lengthSec === 3 || lengthSec === 5 ? lengthSec : 8}
            onChange={onLengthChange}
          />
        </DetailField>
      )}
    </>
  );
};

export const SongWindowPicker = TypedMemo(SongWindowPickerComponent);
